import {
  Alert,
  Container,
  ContentLayout,
  FormField,
  Grid,
  Header,
  SelectProps,
} from '@cloudscape-design/components';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import Input from '../../components/Input';
import Layout from '../../components/Layout';
import Select from '../../components/Select';
import Textarea from '../../components/Textarea';
import TimeInput from '../../components/TimeInput';
import {
  UpdateWebsiteSettingsInput,
  useCreateWebsiteSettingsMutation,
  useGetWebsiteSettingsQuery,
  useUpdateWebsiteSettingsMutation,
} from '../../generated/graphql';
import useNotifications from '../../hooks/useNotifications';
import { ErrorText } from '../../lib/ErrorTexts';
import MetaTags from '../../lib/MetaTags';

type FormValues = {
  id?: string | null;
  title: string;
  author: string;
  description: string;
  keywords: { value: string }[];
  opendayfrom: SelectProps.Option;
  opendayto: SelectProps.Option;
  opentimefrom: string;
  opentimeto: string;
  breakfasttimefrom: string;
  breakfasttimeto: string;
  streetname: string;
  postalcode: string;
  location: string;
  telephonenumber: string;
  email: string;
};

const schema = yup
  .object()
  .shape({
    id: yup.string().notRequired(),
    title: yup.string().required(ErrorText.Required),
    author: yup.string().required(ErrorText.Required),
    description: yup.string().required(ErrorText.Required),
    keywords: yup
      .array()
      .of(
        yup.object({
          value: yup.string().required(ErrorText.Required),
        }),
      )
      .required(ErrorText.Required),
    opendayfrom: yup.object().required(ErrorText.Required),
    opendayto: yup.object().required(ErrorText.Required),
    opentimefrom: yup.string().required(ErrorText.Required),
    opentimeto: yup.string().required(ErrorText.Required),
    breakfasttimefrom: yup.string().required(ErrorText.Required),
    breakfasttimeto: yup.string().required(ErrorText.Required),
    streetname: yup.string().required(ErrorText.Required),
    postalcode: yup
      .string()
      .matches(/^\d{5}$/, ErrorText.PostalCode)
      .required(ErrorText.Required),
    location: yup.string().required(ErrorText.Required),
    telephonenumber: yup.string().required(ErrorText.Required),
    email: yup.string().required(ErrorText.Required),
  })
  .required();

const Website = () => {
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      keywords: [{ value: '' }],
    },
    resolver: yupResolver(schema),
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'keywords',
  });
  const { data: getData } = useGetWebsiteSettingsQuery();
  const [updateWebsiteSettings, { data: updateData, loading: updateLoading }] =
    useUpdateWebsiteSettingsMutation();
  const [createWebsiteSettings, { loading: createLoading }] =
    useCreateWebsiteSettingsMutation();

  useEffect(() => {
    let data: FormValues | undefined | null = undefined;
    if (
      getData &&
      getData.listWebsiteSettings &&
      getData.listWebsiteSettings.items
    )
      data = {
        ...getData.listWebsiteSettings.items[0],
        opendayfrom: getData.listWebsiteSettings.items[0]?.opendayfrom ?? '',
        opendayto: getData.listWebsiteSettings.items[0]?.opendayto ?? '',
        opentimefrom: getData.listWebsiteSettings.items[0]?.opentimefrom ?? '',
        opentimeto: getData.listWebsiteSettings.items[0]?.opentimeto ?? '',
        breakfasttimefrom:
          getData.listWebsiteSettings.items[0]?.breakfasttimefrom ?? '',
        breakfasttimeto:
          getData.listWebsiteSettings.items[0]?.breakfasttimeto ?? '',
      } as FormValues;
    if (updateData && updateData.updateWebsiteSettings)
      data = updateData.updateWebsiteSettings as FormValues;
    if (data) {
      methods.reset(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getData, updateData, methods.reset]);

  const {
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
  } = useNotifications();

  const onSubmitForm = (data: FormValues) => {
    const messageId = uuidv4();
    if (data.id) {
      notifyInProgress({
        id: messageId,
        header: 'Eintrag wird gespeichert...',
      });
      //Update entry
      updateWebsiteSettings({
        variables: { input: data as UpdateWebsiteSettingsInput },
      })
        .then((data) => {
          if (data.data?.updateWebsiteSettings) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              header: 'Gespeichert',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    } else {
      notifyInProgress({
        id: messageId,
        header: 'Eintrag wird gespeichert...',
      });
      //create a new entry
      createWebsiteSettings({ variables: { input: data } })
        .then((data) => {
          if (data.data?.createWebsiteSettings) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              header: 'Gespeichert',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  };

  const disableSubmitButton = useMemo(() => {
    if (updateLoading || createLoading) return true;
    if (isEmpty(methods.formState.dirtyFields)) return true;
    return false;
  }, [createLoading, methods.formState, updateLoading]);

  return (
    <Layout
      notifications={notifications}
      breadcrumbs={[{ text: 'Webseite', href: '/website' }]}
    >
      <MetaTags title="Theo's Bistro" />
      <ContentLayout header={<Header variant="h1">Webseite</Header>}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmitForm)}>
            <SpaceBetween direction="vertical" size="l">
              <div className="flex w-full gap-4 justify-end mt-4">
                <Button formAction="none" onClick={() => methods.reset()}>
                  Zurücksetzen
                </Button>
                <Button
                  variant="primary"
                  disabled={disableSubmitButton}
                  formAction="submit"
                >
                  Speichern
                </Button>
              </div>
              <Container
                header={<Header variant="h2">Allgemeine Angaben</Header>}
              >
                <SpaceBetween direction="vertical" size="m">
                  <FormField label="Öffnungszeiten" stretch>
                    <Grid
                      gridDefinition={[
                        { colspan: 6 },
                        { colspan: 6 },
                        { colspan: 6 },
                        { colspan: 6 },
                      ]}
                    >
                      <Select
                        name="opendayfrom"
                        description="Tag von"
                        options={[
                          {
                            value: '1',
                            label: 'Montag',
                          },
                          {
                            value: '2',
                            label: 'Dienstag',
                          },
                          {
                            value: '3',
                            label: 'Mittwoch',
                          },
                          {
                            value: '4',
                            label: 'Donnerstag',
                          },
                          {
                            value: '5',
                            label: 'Freitag',
                          },
                          {
                            value: '6',
                            label: 'Samstag',
                          },
                          {
                            value: '7',
                            label: 'Sonntag',
                          },
                        ]}
                        stretch
                      />
                      <Select
                        name="opendayto"
                        description="Tag bis"
                        options={[
                          {
                            value: '1',
                            label: 'Montag',
                          },
                          {
                            value: '2',
                            label: 'Dienstag',
                          },
                          {
                            value: '3',
                            label: 'Mittwoch',
                          },
                          {
                            value: '4',
                            label: 'Donnerstag',
                          },
                          {
                            value: '5',
                            label: 'Freitag',
                          },
                          {
                            value: '6',
                            label: 'Samstag',
                          },
                          {
                            value: '7',
                            label: 'Sonntag',
                          },
                        ]}
                      />
                      <TimeInput
                        name="opentimefrom"
                        placeholder="hh:mm"
                        description="Uhrzeit von"
                      />
                      <TimeInput
                        name="opentimeto"
                        placeholder="hh:mm"
                        description="Uhrzeit bis"
                      />
                    </Grid>
                  </FormField>
                  <FormField label="Frühstückszeiten" stretch>
                    <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                      <TimeInput
                        name="breakfasttimefrom"
                        placeholder="hh:mm"
                        description="Uhrzeit von"
                      />
                      <TimeInput
                        name="breakfasttimeto"
                        placeholder="hh:mm"
                        description="Uhrzeit bis"
                      />
                    </Grid>
                  </FormField>
                  <Grid
                    gridDefinition={[
                      { colspan: 12 },
                      { colspan: 3 },
                      { colspan: 9 },
                      { colspan: 12 },
                      { colspan: 12 },
                    ]}
                  >
                    <Input
                      name="streetname"
                      label="Straßenname mit Hausnummer"
                      stretch
                    />
                    <Input name="postalcode" label="Postleitzahl" stretch />
                    <Input name="location" label="Ort" stretch />
                    <Input
                      name="telephonenumber"
                      label="Telefonnummer"
                      stretch
                    />
                    <Input name="email" label="E-Mail Adresse" stretch />
                  </Grid>
                </SpaceBetween>
              </Container>
              <Container
                header={
                  <Header variant="h2">Search Engine Optimization</Header>
                }
              >
                <SpaceBetween direction="vertical" size="m">
                  <Alert
                    statusIconAriaLabel="Info"
                    header="Was ist Search Engine Optimization (SEO)?"
                  >
                    SEO optimiert Websites für Suchmaschinen, verbessert
                    Rankings und Sichtbarkeit durch Keywords, Content,
                    On-Page-Optimierung und Link-Building. Die Inhalte sind
                    nicht sichtbar für den Benutzer sondern werden von
                    sogenannten Webcrawlern (Computerprogramme) ausgelesen.
                  </Alert>
                  <Input
                    name="title"
                    label="Titel"
                    description="Wird für Suchmaschinen und im Browser verwendet."
                    stretch
                  />
                  <Input name="author" label="Author" stretch />
                  <Textarea name="description" label="Beschreibung" stretch />
                  <SpaceBetween direction="vertical" size="s">
                    {fields.map((field, index) => (
                      <div className="flex w-full gap-4" key={field.id}>
                        <Input
                          name={`keywords.${index}.value`}
                          label={index === 0 ? 'Keywords' : undefined}
                          className="flex flex-1 flex-col"
                          stretch
                        />
                        <Button
                          className="self-end"
                          disabled={index === 0}
                          onClick={() => remove(index)}
                        >
                          Löschen
                        </Button>
                      </div>
                    ))}
                    <Button onClick={() => append({ value: '' })}>
                      Keyword hinzufügen
                    </Button>
                  </SpaceBetween>
                </SpaceBetween>
              </Container>
            </SpaceBetween>
            <div className="flex w-full gap-4 justify-end mt-4">
              <Button formAction="none" onClick={() => methods.reset()}>
                Zurücksetzen
              </Button>
              <Button
                variant="primary"
                disabled={disableSubmitButton}
                formAction="submit"
              >
                Speichern
              </Button>
            </div>
          </form>
        </FormProvider>
      </ContentLayout>
    </Layout>
  );
};

export default Website;
